<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-5">
      <heading size="h2" style-size="h4" class="mb-5"
        >Let’s start your virtual job experience in Psychology!
      </heading>
      <img
        src="@/assets/images/experiences/psychology/psych_image.jpg"
        alt=""
        class="my-2 mb-2 float-right"
        role="presentation"
      />

      <p class="mb-5">
        Psychologists apply their expertise to assess and provide treatment for a wide range of
        mental, emotional, and behavioural problems and disorders. They also work with clients to
        optimise their personal, social, and occupational development and performance.
      </p>
      <p class="mb-5">
        All psychologists in Australia must, as a minimum, hold general registration with the
        Psychology Board of Australia.
      </p>
      <p class="mb-5">
        The Board helps ensure that all psychologists working in Australia meet the required
        standards of education, training, supervised practice, ethical and professional standards,
        as well as ongoing professional development.
      </p>
      <p class="mb-5">
        That said, there are many different types of psychologists, each with their own specific
        requirements. These can helpfully be divided into two different categories:
      </p>

      <ol class="numbered-list">
        <li>Psychologists with general registration</li>
        <li>Psychologists with an area of practice endorsement.</li>
      </ol>
      <p class="mb-5">Let’s take a look at each of these in turn.</p>
      <heading size="h2" style-size="h4" class="mb-5"
        >1. Psychologist with General Registration
      </heading>
      <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Description</u></heading>
      <p class="mb-5">
        Psychologists with general registration make up about 60% of Australia’s psychologists. To
        get general registration, psychologists usually need at least 6 years of university training
        and supervised practice. They are also required to undertake ongoing training to keep their
        knowledge and skills up to date.
      </p>
      <p class="mb-5">
        Psychologists with general registration use their knowledge and skills across a wide range
        of areas including mental health and wellbeing, justice and corrections, population health,
        and marketing and communication - to name but a few!
      </p>
      <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Education Level</u></heading>
      <p class="mb-5">
        There are various pathways to becoming eligible for general registration as a psychologist,
        but the minimum is 6 years of education and training.
      </p>
      <p class="mb-5">
        Usually, this involves 4 years of study in psychology at university level, followed by 2
        years of supervised practice as a ‘provisional psychologist’.
      </p>
      <heading size="h2" style-size="h4" class="mb-5"
        >Domestic pathways to general registration:
      </heading>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <!-- me2 issue 4: a complex diagram needs its description as HTML content, not an
               alt attribute, so the steps can carry list markup. The alt-text names the
               diagram and points at that description. -->
          <img
            src="@/assets/images/experiences/psychology/intro1.png"
            alt="Pathways to general registration as a psychologist diagram - described below"
            class="mx-auto img-responsive"
          />
          <details class="diagram-description my-5">
            <summary>Text description of the registration pathway diagram</summary>
            <ol class="numbered-list mt-3">
              <li>Complete a four year sequence of study in psychology in Australia.</li>
              <li>
                Choose one training pathway and apply for provisional registration. The options are:
                <ul class="mt-2">
                  <li>
                    <strong>Higher degree pathway</strong> &mdash; fifth and sixth year Masters
                    degree, or a combined Masters/PhD or Doctorate.
                  </li>
                  <li>
                    <strong>5+1 pathway</strong> &mdash; fifth year degree plus a one year
                    internship.
                  </li>
                  <li>
                    <strong>4+2 pathway</strong> &mdash; a two year internship. This pathway is
                    closing.
                  </li>
                </ul>
              </li>
              <li>
                If you took the 5+1 or 4+2 pathway, pass the National Psychology Exam. The higher
                degree pathway goes straight to the next step.
              </li>
              <li>Apply for general registration.</li>
            </ol>
          </details>
          <p class="mb-10 text-center">
            Sourced from the Psychology Board of Australia 2020 -
            <a href="https://www.psychologyboard.gov.au/Registration/General.aspx" target="_blank"
              >https://www.psychologyboard.gov.au/Registration/General.aspx</a
            >
          </p>
        </div>
      </div>
      <heading size="h2" style-size="h4" class="mb-5"
        >2. Psychologist with an Area of Practice Endorsement
      </heading>

      <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Description</u></heading>
      <p class="mb-5">
        Psychologists with an area of endorsement have a further qualification in a particular area
        of practice. They also have 2 or more years of supervised experience in that area. This
        means that a psychologist with an area of endorsement usually has at least 8 years of
        university education and supervised experience.
      </p>
      <p class="mb-5">
        There are 9 areas of practice endorsement recognised by the Psychology Board of Australia.
      </p>
      <p class="mb-5">
        Psychologists with general registration can be endorsed in one or more of the following
        approved areas of practice:
      </p>
      <ul class="default-list">
        <li>clinical neuropsychology</li>
        <li>clinical psychology</li>
        <li>community psychology</li>
        <li>counselling psychology</li>
        <li>educational and developmental psychology</li>
        <li>forensic psychology</li>
        <li>health psychology</li>
        <li>organisational psychology, and</li>
        <li>sport and exercise psychology.</li>
      </ul>
      <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Education Level</u></heading>
      <p class="mb-5">
        As with general registration, there are several pathways to obtaining an Area of Practice
        Endorsement (AoPE) registration. Generally, AoPE identifies psychologists who have completed
        an approved postgraduate qualification along with supervised training in their chosen area
        of practice from those listed above.
      </p>
      <p class="mb-5">
        Without an AoPE, a psychologist cannot use the protected title associated with the specific
        areas of practice in their title.
      </p>

      <heading size="h2" style-size="h4" class="mb-5"
        >Pathways to area of practice endorsement
      </heading>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <!-- me2 issue 4: second complex diagram on this page. Same treatment as intro1 -
               short alt naming the diagram, full description as HTML below it. -->
          <img
            src="@/assets/images/experiences/psychology/intro2.png"
            alt="Pathways to area of practice endorsement diagram - described below"
            class="mx-auto img-responsive"
          />
          <details class="diagram-description my-5">
            <summary>Text description of the area of practice endorsement diagram</summary>
            <ol class="numbered-list mt-3">
              <li>
                Start from one of two points. Either complete an undergraduate psychology
                qualification and obtain provisional registration as a psychologist, which leads to
                pathways 1 to 3; or already hold general registration as a psychologist, which leads
                to pathways 1 to 4.
              </li>
              <li>
                Complete a postgraduate qualification approved for endorsement, by one of these
                pathways:
                <ul class="mt-2">
                  <li>
                    <strong>Pathway 1 &mdash; Masters degree.</strong> Fifth and sixth year
                    accredited degree, e.g. MPsych.
                  </li>
                  <li>
                    <strong>Pathway 2 &mdash; Combined Masters/PhD.</strong> Fifth and sixth year
                    accredited degree with doctoral thesis, e.g. MPsych/PhD.
                  </li>
                  <li>
                    <strong>Pathway 3 &mdash; Doctoral degree.</strong> Fifth to seventh year
                    accredited degree, e.g. DPsych or PsyD.
                  </li>
                  <li>
                    <strong>Pathway 4 &mdash; Bridging qualification.</strong> Sixth year accredited
                    degree, including bridging to a second endorsement.
                  </li>
                </ul>
              </li>
              <li>
                Obtain general registration, if not already held, and approval to commence the
                registrar program.
              </li>
              <li>
                Complete the registrar program. Its length depends on the pathway taken:
                <ul class="mt-2">
                  <li>Pathway 1 &mdash; 3000 hours.</li>
                  <li>Pathway 2 &mdash; 2250 hours.</li>
                  <li>Pathway 3 &mdash; 1500 hours.</li>
                  <li>
                    Pathway 4 &mdash; 3000 hours, or 2250 hours if bridging to a second endorsement.
                  </li>
                </ul>
              </li>
              <li>Obtain area of practice endorsement on general registration.</li>
            </ol>
          </details>
          <p class="mb-10 text-center">
            Sourced from Psychology Board of Australia
            <a
              aria-label="link to pathways to endorsement"
              href="https://www.psychologyboard.gov.au/Endorsement/Pathways-to-endorsement.aspx"
              target="_blank"
            ></a>
          </p>
        </div>
      </div>
      <p class="mb-5">
        To find out more on how to successfully navigate the often complicated information available
        on becoming a psychologist in Australia, check out this video:
      </p>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <custom-video
            aria-label="Watch becoming becoming registered video"
            elem-id="video-btn-0"
            :video-image="videoTrigger1"
            :video-url="videos[1]"
          />
        </div>
      </div>
    </vx-card>

    <vx-card>
      <heading size="h2" style-size="h4" class="mb-5">TASKS – Psychologist</heading>
      <p class="mb-5">
        (Click the down arrows on the right to get descriptions and examples of each one)
      </p>
      <!-- accordion 1 -->
      <accordion-panel
        aria-title="Collecting data about clients and assessing their cognitive, behavioural, and emotional
            disorders"
      >
        <template v-slot:title>
          <div>
            Collecting data about clients and assessing their cognitive, behavioural, and emotional
            disorders
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> trying to understand the thoughts, emotions, and behaviours
            of a client by observing, discussing, and carrying out assessments. This information
            will then be used to help treat the client.
          </p>
          <p class="mb-5">
            <strong>An example</strong> of this could be administering personality, performance,
            aptitude, or intelligence tests to help develop theories to treat a new client. Also, by
            observing clients closely, psychologists try to detect behaviour patterns and understand
            the relationships between events to understand their clients better.
          </p>
        </template>
      </accordion-panel>
      <!-- accordion 2 -->
      <accordion-panel
        aria-title="Administering and interpreting diagnostic tests and formulating plans for treatment"
      >
        <template v-slot:title>
          <div slot="header">
            Administering and interpreting diagnostic tests and formulating plans for treatment
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> having the technical knowledge to understand test results
            and being able to decide on a suitable course of treatment for the client.
          </p>
          <p class="mb-5">
            <strong>An example</strong> of this could be administering the Wechsler Intelligence
            Test to a child to assess their cognitive strengths and weaknesses. This information is
            then shared with the child’s school to help teachers best meet the specific needs of the
            child within their school.
          </p>
        </template>
      </accordion-panel>
      <!-- accordion 3 -->
      <accordion-panel
        aria-title="Developing, administering, and evaluating individual and group treatment programs"
      >
        <template v-slot:title>
          <div>
            Developing, administering, and evaluating individual and group treatment programs
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> having familiarity with program development, administration,
            and evaluation of best practices. This will require keeping skills and knowledge of
            current research in these areas up to date.
          </p>
          <p class="mb-5">
            <strong>An example</strong> of this could be drawing up a treatment plan for a client
            with severe depression using their detailed personal history and psychological
            information gathered from assessments. Treatment goals are set and a timeline for
            meeting these goals is established. The client’s progress and response to the treatment
            program are continuously tracked, with the program’s effectiveness constantly evaluated
            in the process.
          </p>
        </template>
      </accordion-panel>
      <!-- accordion 4 -->
      <accordion-panel
        aria-title="Consulting with other professionals on details of cases and treatment plans"
      >
        <template v-slot:title>
          <div>Consulting with other professionals on details of cases and treatment plans</div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> discussing a client’s case details and treatment options
            with other psychologists and members of related professions. The purpose of this is to
            get the best possible outcome for the client. This is done in such a way that protects
            the client’s identity and is in line with the relevant code of ethics.
          </p>
          <p class="mb-5">
            <strong>An example</strong> of this could be meeting with a new client who has been
            self-harming and who talks constantly about committing suicide. Very worried and unsure
            of what to do next, you consult with a very experienced colleague with an AoPE in
            clinical psychology to find the best course of action to take with the depressed client.
          </p>
        </template>
      </accordion-panel>
      <!-- accordion 5 -->
      <accordion-panel
        aria-title="Conducting research studies of motivation in learning, group performance, and individual
            differences in mental abilities and educational performance"
      >
        <template v-slot:title>
          <div>
            Conducting research studies of motivation in learning, group performance, and individual
            differences in mental abilities and educational performance
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> researching to find out more about what motivates people to
            learn, the differences in people’s ability to learn, and how these affect how well
            students do at school and university.
          </p>
          <p class="mb-5">
            <strong>An example</strong> of this might be taking part in a study to assess the
            intelligence and motivations of several students using standardised tests, tracking
            student performance over several years, and making comparisons between individual
            students and between different groups. This information is then made available to help
            in designing effective learning programs for students.
          </p>
        </template>
      </accordion-panel>
      <!-- accordion 6 -->
      <accordion-panel
        aria-title="Collecting data and analysing characteristics of students and recommending educational programs"
      >
        <template v-slot:title>
          <div>
            Collecting data and analysing characteristics of students and recommending educational
            programs
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> knowing how to gather up information about students and how
            to look at that information to make decisions on what type of learning will work best
            for individual students or groups of students.
          </p>
          <p class="mb-5">
            <strong>An example</strong> of this could be working closely with the Australian
            Curriculum, Assessment and Reporting Authority (ACARA) to assist in reviewing the
            Australian Curriculum for use in schools.
          </p>
        </template>
      </accordion-panel>
      <!-- accordion 7 -->
      <accordion-panel
        aria-title="Formulating achievement, diagnostic and predictive tests for use by teachers in planning methods and content of instruction"
      >
        <template v-slot:title>
          <div>
            Formulating achievement, diagnostic and predictive tests for use by teachers in planning
            methods and content of instruction
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> working to produce instruments and tools to help teachers
            deliver effective lessons in the classroom.
          </p>
          <p class="mb-5">
            <strong>An example</strong> of this could be working on a university project aimed at
            reviewing the research on science education delivery to develop practical tools to
            assist teachers in the classroom. Rather than communicating the latest research findings
            to teachers, the team works to develop practical tools to help teachers enhance
            students’ science learning.
          </p>
        </template>
      </accordion-panel>
      <!-- accordion 8 -->
      <accordion-panel
        aria-title=" Developing interview techniques, psychological tests, and other aids in workplace
            selection, placement, appraisal, and promotion"
      >
        <template v-slot:title>
          <div>
            Developing interview techniques, psychological tests, and other aids in workplace
            selection, placement, appraisal, and promotion
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> working on a range of projects aimed at helping companies
            select the best person for various jobs, as well as helping to assess workplace
            performance and identify those ready for promotion.
          </p>
          <p class="mb-5">
            <strong>An example</strong> could be working as a consultant with the human resources
            department of a large multinational to develop an effective performance management
            system for use throughout the company.
          </p>
        </template>
      </accordion-panel>
      <!-- accordion 9 -->
      <accordion-panel
        aria-title="Conducting surveys and research studies on job design, workgroups, morale, motivation,
          supervision, and management"
      >
        <template v-slot:title>
          <div>
            Conducting surveys and research studies on job design, workgroups, morale, motivation,
            supervision, and management
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> developing a range of tools to gather data on various
            aspects of workplace operations.
          </p>
          <p class="mb-5">
            <strong>An example</strong> could involve designing and delivering a management
            performance survey to evaluate how employees at a company feel their leadership is
            performing. The information gathered will be used to improve internal communication
            within the company and increase the overall quality of leadership.
          </p>
        </template>
      </accordion-panel>
      <!-- accordion 10 -->
      <accordion-panel
        aria-title="Performing job analyses and establishing job requirements by observing and interviewing
                  employees and managers"
      >
        <template v-slot:title>
          <div>
            Performing job analyses and establishing job requirements by observing and interviewing
            employees and managers
          </div>
        </template>
        <template v-slot:content>
          <p class="mb-5">
            <strong>This means</strong> gathering data related to the tasks performed in specific
            jobs to improve the overall efficiency of a company.
          </p>
          <p class="mb-5">
            <strong>An example</strong> could involve shadowing an employee or a manager for a day
            as they go about their duties to analyse what knowledge, skills, and abilities are
            required to perform the job well.
          </p>
        </template>
      </accordion-panel>
    </vx-card>

    <vx-card>
      <heading size="h2" style-size="h4" class="mb-5">Quick Details</heading>
      <div class="vx-row my-5 flex items-center justify-center">
        <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
          <img
            src="@/assets/images/experiences/psychology/jo1.png"
            class="mx-auto img-responsive"
            alt="Psychologists and Psychotherapists job outlook: $1,857 weekly pay, very strong future growth, 26,800 workers, very high skill level, 52% full-time, 43 hours average full-time week, average age 44, 80% female."
          />
          <p class="mb-10 text-center">
            Sourced from <a href="https://www.joboutlook.gov.au/" target="_blank">Job Outlook</a>
          </p>
        </div>
      </div>
      <vs-alert color="dark" title="Caution" active="true">
        The Australian jobs market is changing in response to the COVID-19 pandemic. These estimates
        do not take account of the impact of COVID-19. They may not reflect the current jobs market
        and should be used and interpreted with extreme caution.
      </vs-alert>
    </vx-card>

    <vx-card>
      <heading size="h2" style-size="h4" class="mb-5"
        >Highlights of Becoming a Psychologist
      </heading>
      <heading size="h3" style-size="h5" class="mb-5">1. Be Your Own Boss</heading>
      <p class="mb-5">
        Once you have become established, you could set up your own private therapy practice. This
        is a great option if you have an entrepreneurial spirit. As a self-employed person you can
        set your own working schedule and take some control over how much you work and earn.
      </p>
      <heading size="h3" style-size="h5" class="mb-5">2. Work with Different People</heading>
      <p class="mb-5">
        As a psychologist, you’ll get to work with people from all walks of life. No two clients
        will be the same, so each work day will be filled with new challenges and experiences.
      </p>
      <heading size="h3" style-size="h5" class="mb-5">3. Work in a Rewarding Profession</heading>
      <p class="mb-5">
        Your work as a psychologist will be filled with meaning. You will help people suffering from
        mental and emotional problems improve the quality of their lives. While things can get
        stressful on occasion, knowing that you are helping others is very rewarding professionally.
      </p>
    </vx-card>
  </div>
</template>

<script>
import AccordionPanel from '@/components/AccordionPanel';
import store from '@/store/store';
import CustomVideo from '@/components/CustomVideo';

export default {
  components: {
    AccordionPanel,
    CustomVideo,
  },
  data() {
    return {
      cover_img: require('@/assets/images/other/cover.jpg'),
      popupActive: false,
      videoTrigger1: require('@/assets/images/experiences/psychology/becoming_a_registered_psychologist_trigger.jpg'),
      videos: {
        1: 'https://www.youtube.com/embed/atK6U16fLqA',
      },
    };
  },
  methods: {
    showVideoFile(video) {
      store.dispatch('popup/showIframePopup', video);
    },
  },
};
</script>
